import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { LISTING_BADGE_LISTING_FIELDS } from '../../config/r2rListingBadges';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser, setCurrentUserHasOrders } from '../../ducks/user.duck';
import { transactionLineItems } from '../../util/api';
import {
  listingCardSponsorHighlightFields,
  listingPageAuthorUserFields,
} from '../../util/avatarImageQueryFields';
import { denormalisedResponseEntities } from '../../util/data';
import {
  bookingTimeUnits,
  findNextBoundary,
  getStartOf,
  monthIdString,
  stringifyDateToISO8601,
} from '../../util/dates';
import { storableError } from '../../util/errors';
import { LISTING_CARD_MEDIA_FIELDS } from '../../util/listingMedia';
import * as log from '../../util/log';
import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import {
  listingCardImageFields,
  listingCardImageVariantParams,
  listingHeroImageFields,
  listingHeroImageVariantParams,
} from '../../util/listingImageVariants';
import { getSellerPrivateNote } from '../../util/sellerPrivateNote';
import { REVIEW_TYPE_OF_PROVIDER } from '../../util/types';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import {
  hasPermissionToInitiateTransactions,
  hasPermissionToViewData,
  isUserAuthorized,
} from '../../util/userHelpers';
import {
  getProcess,
  isBookingProcessAlias,
  isNegotiationProcessAlias,
  OFFER,
} from '../../transactions/transaction';

const { UUID } = sdkTypes;
const LISTING_PAGE_SELLER_REVIEW_LIMIT = 2;
/** Fetch one extra so excluding the current listing still yields up to this many cards. */
export const LISTING_PAGE_MORE_FROM_SELLER_LIMIT = 8;
const LISTING_PAGE_AUTHOR_LISTINGS_PER_PAGE = LISTING_PAGE_MORE_FROM_SELLER_LIMIT + 1;
const MINUTE_IN_MS = 1000 * 60;

// Day-based time slots queries are cached for 1 minute.
const removeOutdatedDateData = timeSlotsForDate => {
  const now = new Date().getTime();
  const minuteAgo = now - MINUTE_IN_MS;
  return Object.fromEntries(
    Object.entries(timeSlotsForDate).filter(([dateId, data]) => {
      return data.fetchedAt && data.fetchedAt > minuteAgo;
    })
  );
};

// ================ Async Thunks ================ //

//////////////////
// Show Listing //
//////////////////
const showListingPayloadCreator = ({ listingId, config, isOwn = false }, thunkAPI) => {
  const { dispatch, rejectWithValue, extra: sdk } = thunkAPI;
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  // Current user entity is fetched in a bit lazy fashion, since it's not tied to returned Promise chain.
  const fetchCurrentUserOptions = {
    updateHasListings: false,
    updateNotifications: false,
  };
  dispatch(fetchCurrentUser(fetchCurrentUserOptions));

  const params = {
    id: listingId,
    include: ['author', 'author.profileImage', 'images', 'currentStock'],
    'fields.user': listingPageAuthorUserFields,
    'fields.image': [
      // Scaled variants for large images
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',
      ...listingHeroImageFields(),

      // Cropped variants for listing thumbnail images
      ...listingCardImageFields(variantPrefix),
      `variants.${variantPrefix}-4x`,
      `variants.${variantPrefix}-6x`,

      // Social media
      'variants.facebook',
      'variants.twitter',

      // Avatars
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...listingCardImageVariantParams(variantPrefix, aspectRatio),
    ...listingHeroImageVariantParams(),
    ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
  };

  const show = isOwn ? sdk.ownListings.show(params) : sdk.listings.show(params);

  return show
    .then(data => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };
      dispatch(addMarketplaceEntities(data, sanitizeConfig));
      return data;
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const showListingThunk = createAsyncThunk(
  'ListingPage/showListing',
  showListingPayloadCreator
);
// Backward compatible wrapper for the thunk
export const showListing = (listingId, config, isOwn = false) => (dispatch, getState, sdk) => {
  return dispatch(showListingThunk({ listingId, config, isOwn })).unwrap();
};

///////////////////
// Fetch Reviews //
///////////////////
export const fetchReviewsThunk = createAsyncThunk(
  'ListingPage/fetchReviews',
  ({ authorId }, { rejectWithValue, extra: sdk }) => {
    return sdk.reviews
      .query({
        subject_id: authorId,
        state: 'public',
        include: ['author', 'author.profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      })
      .then(response => {
        const reviews = denormalisedResponseEntities(response);
        return reviews
          .filter(r => r?.attributes?.type === REVIEW_TYPE_OF_PROVIDER)
          .slice(0, LISTING_PAGE_SELLER_REVIEW_LIMIT);
      })
      .catch(e => {
        return rejectWithValue(storableError(e));
      });
  }
);

export const fetchReviews = authorId => (dispatch, getState, sdk) => {
  return dispatch(fetchReviewsThunk({ authorId })).unwrap();
};

/////////////////////////////////
// Fetch author listings (rail) //
/////////////////////////////////

/**
 * Drop the current listing (and its included images) from an author-listings query
 * response before merging into marketplace entities. Card-sized `fields.image` would
 * otherwise replace gallery variants on the open listing (variants are not deep-merged).
 */
export const omitListingFromQueryResponse = (response, excludeListingId) => {
  const excludeUuid = excludeListingId?.uuid;
  if (!excludeUuid || !response?.data) {
    return response;
  }

  const listings = Array.isArray(response.data.data) ? response.data.data : [];
  const included = Array.isArray(response.data.included) ? response.data.included : [];
  const excludedListing = listings.find(l => l.id?.uuid === excludeUuid);
  const excludedImageIds = new Set(
    (excludedListing?.relationships?.images?.data || []).map(ref => ref?.id?.uuid).filter(Boolean)
  );

  return {
    ...response,
    data: {
      ...response.data,
      data: listings.filter(l => l.id?.uuid !== excludeUuid),
      included: included.filter(resource => {
        if (resource?.type === 'image' && excludedImageIds.has(resource.id?.uuid)) {
          return false;
        }
        return true;
      }),
    },
  };
};

export const fetchAuthorListingsThunk = createAsyncThunk(
  'ListingPage/fetchAuthorListings',
  ({ authorId, config, excludeListingId }, { dispatch, getState, rejectWithValue, extra: sdk }) => {
    const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } =
      config?.layout?.listingImage || {};
    const aspectRatio = aspectHeight / aspectWidth;

    // Do not include `author` — ListingCard uses showAuthorInfo={false}, and a sparse
    // fields.user payload would shallow-merge over the full author from showListing.
    return sdk.listings
      .query({
        author_id: authorId,
        page: 1,
        perPage: LISTING_PAGE_AUTHOR_LISTINGS_PER_PAGE,
        sort: 'createdAt',
        include: ['images'],
        'fields.listing': [
          ...new Set([
            'title',
            'price',
            'deleted',
            'state',
            'publicData.listingType',
            'publicData.categoryLevel1',
            'publicData.categoryLevel2',
            'publicData.categoryLevel3',
            'publicData.transactionProcessAlias',
            'publicData.unitType',
            'publicData.cardStyle',
            'publicData.pickupEnabled',
            'publicData.shippingEnabled',
            'publicData.priceVariationsEnabled',
            'publicData.priceVariants',
            ...listingCardSponsorHighlightFields,
            ...LISTING_BADGE_LISTING_FIELDS,
            ...LISTING_CARD_MEDIA_FIELDS,
          ]),
        ],
        'fields.image': listingCardImageFields(variantPrefix),
        ...listingCardImageVariantParams(variantPrefix, aspectRatio),
      })
      .then(response => {
        // Ignore stale responses after navigating to another listing page.
        const pageListingId = getState().ListingPage?.id;
        const excludeUuid = excludeListingId?.uuid;
        if (excludeUuid && pageListingId?.uuid && excludeUuid !== pageListingId.uuid) {
          return { listingRefs: [], totalItems: null, stale: true };
        }

        const safeResponse = omitListingFromQueryResponse(response, excludeListingId);
        const listings = safeResponse.data.data;
        const listingRefs = listings
          .filter(l => !l.attributes.deleted && l.attributes.state === 'published')
          .filter(l => !excludeUuid || l.id?.uuid !== excludeUuid)
          .slice(0, LISTING_PAGE_MORE_FROM_SELLER_LIMIT)
          .map(({ id, type }) => ({ id, type }));
        const total = response?.data?.meta?.totalItems;
        dispatch(addMarketplaceEntities(safeResponse));
        return {
          listingRefs,
          totalItems: typeof total === 'number' ? total : null,
        };
      })
      .catch(e => {
        return rejectWithValue(storableError(e));
      });
  }
);

export const fetchAuthorListings = (authorId, config, excludeListingId) => (
  dispatch,
  getState,
  sdk
) => {
  return dispatch(fetchAuthorListingsThunk({ authorId, config, excludeListingId })).unwrap();
};

//////////////////////
// Fetch Time Slots //
//////////////////////

const timeSlotsRequest = createAsyncThunk(
  'ListingPage/timeSlotsRequest',
  (params, { extra: sdk }) => {
    return sdk.timeslots.query(params).then(response => {
      return denormalisedResponseEntities(response);
    });
  }
);

const fetchTimeSlotsPayloadCreator = ({ listingId, start, end, timeZone, options }, thunkAPI) => {
  const { dispatch, getState } = thunkAPI;
  const { extraQueryParams = null, useFetchTimeSlotsForDate = false } = options || {};

  // The maximum pagination page size for timeSlots is 500
  const extraParams = extraQueryParams || {
    perPage: 500,
    page: 1,
  };

  // For small time units, we fetch the data per date.
  // This is to avoid fetching too much data (with 15 minute intervals, there can be 24*4*31 = 2928 time slots)
  if (useFetchTimeSlotsForDate) {
    const dateId = stringifyDateToISO8601(start, timeZone);
    const dateData = getState().ListingPage.timeSlotsForDate[dateId];
    const minuteAgo = new Date().getTime() - MINUTE_IN_MS;
    const hasRecentlyFetchedData = dateData?.fetchedAt > minuteAgo;
    if (hasRecentlyFetchedData) {
      return Promise.resolve(dateData?.timeSlots || []);
    }

    return dispatch(timeSlotsRequest({ listingId, start, end, ...extraParams }))
      .then(response => {
        return response.payload;
      })
      .catch(e => {
        return [];
      });
  } else {
    return dispatch(timeSlotsRequest({ listingId, start, end, ...extraParams }))
      .then(response => {
        return response.payload;
      })
      .catch(e => {
        return [];
      });
  }
};

export const fetchTimeSlotsThunk = createAsyncThunk(
  'ListingPage/fetchTimeSlots',
  fetchTimeSlotsPayloadCreator
);
// Backward compatible wrapper for the thunk
export const fetchTimeSlots = (listingId, start, end, timeZone, options) => (
  dispatch,
  getState,
  sdk
) => {
  return dispatch(fetchTimeSlotsThunk({ listingId, start, end, timeZone, options })).unwrap();
};

//////////////////
// Send Inquiry //
//////////////////
const sendInquiryPayloadCreator = (
  { listing, message },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  const processAlias = listing?.attributes?.publicData?.transactionProcessAlias;
  if (!processAlias) {
    const error = new Error('No transaction process attached to listing');
    log.error(error, 'listing-process-missing', {
      listingId: listing?.id?.uuid,
    });
    return rejectWithValue(storableError(error));
  }

  const listingId = listing?.id;
  const [processName, alias] = processAlias.split('/');

  // Negotiation process does not support inquiry for customer role on OFFER flog (only request for quote is supported)
  const isNegotiationProcess = isNegotiationProcessAlias(processAlias);
  const unitType = listing?.attributes?.publicData?.unitType || '';
  if (isNegotiationProcess && unitType === OFFER) {
    return rejectWithValue(
      storableError(
        new Error(
          'Negotiation process with unit type OFFER does not support inquiry for customer role'
        )
      )
    );
  }

  const transitions = getProcess(processName)?.transitions;

  const bodyParams = {
    transition: transitions.INQUIRE,
    processAlias,
    params: { listingId },
  };
  return sdk.transactions
    .initiate(bodyParams)
    .then(response => {
      const transactionId = response.data.data.id;

      // Send the message to the created transaction
      return sdk.messages.send({ transactionId, content: message }).then(() => {
        dispatch(setCurrentUserHasOrders());
        return transactionId;
      });
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const sendInquiryThunk = createAsyncThunk(
  'ListingPage/sendInquiry',
  sendInquiryPayloadCreator
);
// Backward compatible wrapper for the thunk
export const sendInquiry = (listing, message) => (dispatch, getState, sdk) => {
  return dispatch(sendInquiryThunk({ listing, message })).unwrap();
};

// Helper function for loadData call.
// Note: listing could be ownListing entity too
const fetchMonthlyTimeSlots = (dispatch, listing) => {
  const hasWindow = typeof window !== 'undefined';
  const { availabilityPlan, publicData } = listing?.attributes || {};
  const tz = availabilityPlan?.timezone;

  // Fetch time-zones on client side only.
  if (hasWindow && listing.id && !!tz) {
    const { unitType, priceVariants, startTimeInterval } = publicData || {};
    const now = new Date();
    const startOfToday = getStartOf(now, 'day', tz);
    const isFixed = unitType === 'fixed';

    const timeUnit = startTimeInterval
      ? bookingTimeUnits[startTimeInterval]?.timeUnit
      : unitType === 'hour'
      ? 'hour'
      : 'day';
    const nextBoundary = findNextBoundary(now, 1, timeUnit, tz);

    const nextMonth = getStartOf(nextBoundary, 'month', tz, 1, 'months');
    const nextAfterNextMonth = getStartOf(nextMonth, 'month', tz, 1, 'months');

    const variants = priceVariants || [];
    const bookingLengthInMinutes = variants.reduce((min, priceVariant) => {
      return Math.min(min, priceVariant.bookingLengthInMinutes);
    }, Number.MAX_SAFE_INTEGER);

    const nextMonthEnd = isFixed
      ? getStartOf(nextMonth, 'minute', tz, bookingLengthInMinutes, 'minutes')
      : nextMonth;
    const followingMonthEnd = isFixed
      ? getStartOf(nextAfterNextMonth, 'minute', tz, bookingLengthInMinutes, 'minutes')
      : nextAfterNextMonth;

    const minDurationStartingInInterval = isFixed ? bookingLengthInMinutes : 60;

    const options = intervalAlign => {
      return ['fixed', 'hour'].includes(unitType)
        ? {
            extraQueryParams: {
              intervalDuration: 'P1D',
              intervalAlign,
              maxPerInterval: 1,
              minDurationStartingInInterval,
              perPage: 31,
              page: 1,
            },
          }
        : null;
    };

    return Promise.all([
      dispatch(fetchTimeSlots(listing.id, nextBoundary, nextMonthEnd, tz, options(startOfToday))),
      dispatch(fetchTimeSlots(listing.id, nextMonth, followingMonthEnd, tz, options(nextMonth))),
    ]);
  }

  // By default return an empty array
  return Promise.all([]);
};

//////////////////////////////////
// Fetch Transaction Line Items //
//////////////////////////////////
const fetchTransactionLineItemsPayloadCreator = (
  { orderData, listingId, isOwnListing },
  { rejectWithValue }
) => {
  return transactionLineItems({ orderData, listingId, isOwnListing })
    .then(response => {
      return response.data;
    })
    .catch(e => {
      log.error(e, 'fetching-line-items-failed', {
        listingId: listingId.uuid,
        orderData,
        statusText: e.statusText,
      });
      return rejectWithValue(storableError(e));
    });
};

export const fetchTransactionLineItemsThunk = createAsyncThunk(
  'ListingPage/fetchTransactionLineItems',
  fetchTransactionLineItemsPayloadCreator
);
// Backward compatible wrapper for the thunk
export const fetchTransactionLineItems = ({ orderData, listingId, isOwnListing }) => dispatch => {
  return dispatch(fetchTransactionLineItemsThunk({ orderData, listingId, isOwnListing })).unwrap();
};

// ================ Slice ================ //

const initialState = {
  id: null,
  showListingError: null,
  sellerPrivateNote: null,
  reviews: [],
  fetchReviewsError: null,
  authorListingRefs: [],
  authorPublishedListingCount: null,
  fetchAuthorListingsError: null,
  monthlyTimeSlots: {
    // '2022-03': {
    //   timeSlots: [],
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  timeSlotsForDate: {
    // For small time units, we fetch monthly time slots with sparse mode for calendar view
    // and when the user clicks on a day, we make a full time slot query. This is for that purpose.
    // '2025-02-03': {
    //   timeSlots: [],
    //   fetchedAt: 1738569600000,
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  lineItems: null,
  fetchLineItemsInProgress: false,
  fetchLineItemsError: null,
  sendInquiryInProgress: false,
  sendInquiryError: null,
  inquiryModalOpenForListingId: null,
};

const listingPageSlice = createSlice({
  name: 'ListingPage',
  initialState,
  reducers: {
    setInitialValues: (state, action) => {
      return { ...initialState, ...action.payload };
    },
    setSellerPrivateNote: (state, action) => {
      state.sellerPrivateNote = action.payload || null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(showListingThunk.pending, (state, action) => {
        state.id = action.meta.arg.listingId;
        state.showListingError = null;
        state.sellerPrivateNote = null;
        state.authorListingRefs = [];
        state.authorPublishedListingCount = null;
        state.fetchAuthorListingsError = null;
      })
      .addCase(showListingThunk.fulfilled, (state, action) => {
        // Data is handled by addMarketplaceEntities in the thunk
        state.id = action.payload.data.data.id;
        // ownListings.show includes privateData (draft/pending / no-viewing-rights).
        if (action.meta.arg.isOwn) {
          state.sellerPrivateNote = getSellerPrivateNote({
            attributes: action.payload.data.data.attributes,
          });
        }
      })
      .addCase(showListingThunk.rejected, (state, action) => {
        state.showListingError = action.payload;
      })
      .addCase(fetchReviewsThunk.pending, state => {
        state.fetchReviewsError = null;
      })
      .addCase(fetchReviewsThunk.fulfilled, (state, action) => {
        state.reviews = action.payload;
      })
      .addCase(fetchReviewsThunk.rejected, (state, action) => {
        state.fetchReviewsError = action.payload;
      })
      .addCase(fetchAuthorListingsThunk.pending, state => {
        // Keep previous refs/count while refetching to avoid empty-state flicker.
        state.fetchAuthorListingsError = null;
      })
      .addCase(fetchAuthorListingsThunk.fulfilled, (state, action) => {
        const excludeUuid = action.meta.arg.excludeListingId?.uuid;
        // Drop stale fulfills from a previous listing page navigation.
        if (
          action.payload?.stale ||
          (excludeUuid && state.id?.uuid && excludeUuid !== state.id.uuid)
        ) {
          return;
        }
        state.authorListingRefs = action.payload?.listingRefs || [];
        state.authorPublishedListingCount =
          typeof action.payload?.totalItems === 'number' ? action.payload.totalItems : null;
      })
      .addCase(fetchAuthorListingsThunk.rejected, (state, action) => {
        const excludeUuid = action.meta.arg.excludeListingId?.uuid;
        if (excludeUuid && state.id?.uuid && excludeUuid !== state.id.uuid) {
          return;
        }
        state.authorListingRefs = [];
        state.authorPublishedListingCount = null;
        state.fetchAuthorListingsError = action.payload;
      })
      .addCase(fetchTimeSlotsThunk.pending, (state, action) => {
        const { options, start, timeZone } = action.meta.arg;
        const { useFetchTimeSlotsForDate = false } = options || {};

        if (useFetchTimeSlotsForDate) {
          const dateId = stringifyDateToISO8601(start, timeZone);
          state.timeSlotsForDate = removeOutdatedDateData(state.timeSlotsForDate);
          if (!state.timeSlotsForDate[dateId]) {
            state.timeSlotsForDate[dateId] = {};
          }
          state.timeSlotsForDate[dateId].fetchTimeSlotsError = null;
          state.timeSlotsForDate[dateId].fetchedAt = null;
          state.timeSlotsForDate[dateId].fetchTimeSlotsInProgress = true;
          state.timeSlotsForDate[dateId].timeSlots = [];
        } else {
          const monthId = monthIdString(start, timeZone);
          if (!state.monthlyTimeSlots[monthId]) {
            state.monthlyTimeSlots[monthId] = {};
          }
          state.monthlyTimeSlots[monthId].fetchTimeSlotsError = null;
          state.monthlyTimeSlots[monthId].fetchTimeSlotsInProgress = true;
        }
      })
      .addCase(fetchTimeSlotsThunk.fulfilled, (state, action) => {
        const { options, start, timeZone } = action.meta.arg;
        const { useFetchTimeSlotsForDate = false } = options || {};

        if (useFetchTimeSlotsForDate) {
          const dateId = stringifyDateToISO8601(start, timeZone);
          if (!state.timeSlotsForDate[dateId]) {
            state.timeSlotsForDate[dateId] = {};
          }
          state.timeSlotsForDate[dateId].fetchTimeSlotsInProgress = false;
          state.timeSlotsForDate[dateId].fetchedAt = new Date().getTime();
          state.timeSlotsForDate[dateId].timeSlots = action.payload;
        } else {
          const monthId = monthIdString(start, timeZone);
          if (!state.monthlyTimeSlots[monthId]) {
            state.monthlyTimeSlots[monthId] = {};
          }
          state.monthlyTimeSlots[monthId].fetchTimeSlotsInProgress = false;
          state.monthlyTimeSlots[monthId].timeSlots = action.payload;
        }
      })
      .addCase(fetchTimeSlotsThunk.rejected, (state, action) => {
        const { options, start, timeZone } = action.meta.arg;
        const { useFetchTimeSlotsForDate = false } = options || {};

        if (useFetchTimeSlotsForDate) {
          const dateId = stringifyDateToISO8601(start, timeZone);
          if (!state.timeSlotsForDate[dateId]) {
            state.timeSlotsForDate[dateId] = {};
          }
          state.timeSlotsForDate[dateId].fetchTimeSlotsInProgress = false;
          state.timeSlotsForDate[dateId].fetchTimeSlotsError = action.payload;
        } else {
          const monthId = monthIdString(start, timeZone);
          if (!state.monthlyTimeSlots[monthId]) {
            state.monthlyTimeSlots[monthId] = {};
          }
          state.monthlyTimeSlots[monthId].fetchTimeSlotsInProgress = false;
          state.monthlyTimeSlots[monthId].fetchTimeSlotsError = action.payload;
        }
      })
      .addCase(sendInquiryThunk.pending, state => {
        state.sendInquiryInProgress = true;
        state.sendInquiryError = null;
      })
      .addCase(sendInquiryThunk.fulfilled, state => {
        state.sendInquiryInProgress = false;
        state.inquiryModalOpenForListingId = null;
      })
      .addCase(sendInquiryThunk.rejected, (state, action) => {
        state.sendInquiryInProgress = false;
        state.sendInquiryError = action.payload;
      })
      .addCase(fetchTransactionLineItemsThunk.pending, state => {
        state.fetchLineItemsInProgress = true;
        state.fetchLineItemsError = null;
      })
      .addCase(fetchTransactionLineItemsThunk.fulfilled, (state, action) => {
        state.fetchLineItemsInProgress = false;
        state.lineItems = action.payload;
      })
      .addCase(fetchTransactionLineItemsThunk.rejected, (state, action) => {
        state.fetchLineItemsInProgress = false;
        state.fetchLineItemsError = action.payload;
      });
  },
});

export const { setInitialValues, setSellerPrivateNote } = listingPageSlice.actions;

export default listingPageSlice.reducer;

// ================ Load data ================ //

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const listingId = new UUID(params.id);
  const state = getState();
  const currentUser = state.user?.currentUser;
  const inquiryModalOpenForListingId =
    isUserAuthorized(currentUser) && hasPermissionToInitiateTransactions(currentUser)
      ? state.ListingPage.inquiryModalOpenForListingId
      : null;

  // Clear old line-items
  dispatch(setInitialValues({ lineItems: null, inquiryModalOpenForListingId }));

  const ownListingVariants = [LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT];
  if (ownListingVariants.includes(params.variant)) {
    return dispatch(showListing(listingId, config, true));
  }

  // In private marketplace mode, this page won't fetch data if the user is unauthorized
  const isAuthorized = currentUser && isUserAuthorized(currentUser);
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const canFetchData = !isPrivateMarketplace || (isPrivateMarketplace && isAuthorized);
  if (!canFetchData) {
    return Promise.resolve();
  }

  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const showPromise = hasNoViewingRights
    ? dispatch(showListing(listingId, config, true))
    : dispatch(showListing(listingId, config));

  return showPromise.then(listingResponse => {
    const listing = listingResponse?.data?.data;
    // API responses use relationships; some tests/fixtures attach a denormalized author.
    const authorId = listing?.relationships?.author?.data?.id || listing?.author?.id;
    const transactionProcessAlias = listing?.attributes?.publicData?.transactionProcessAlias || '';
    const isAuthor =
      !!currentUser?.id?.uuid && !!authorId?.uuid && currentUser.id.uuid === authorId.uuid;

    const followUps = [];
    // Public listings.show omits privateData; fetch the note via ownListings for the author.
    // Soft-fail: never use showListingThunk here (rejected path sets showListingError), and never
    // block the listing page if this optional note fetch fails. Store on ListingPage slice so the
    // UI re-renders when the note arrives (marketplaceData alone is not selected by this page).
    if (isAuthor && !hasNoViewingRights) {
      followUps.push(
        sdk.ownListings
          .show({
            id: listingId,
            'fields.listing': ['privateData'],
          })
          .then(data => {
            dispatch(
              setSellerPrivateNote(
                getSellerPrivateNote({ attributes: data?.data?.data?.attributes })
              )
            );
            return data;
          })
          .catch(() => {
            dispatch(setSellerPrivateNote(null));
            return null;
          })
      );
    }
    if (!hasNoViewingRights && authorId) {
      followUps.push(dispatch(fetchReviews(authorId)));
      followUps.push(dispatch(fetchAuthorListings(authorId, config, listingId)));
    }
    if (isBookingProcessAlias(transactionProcessAlias) && !hasNoViewingRights) {
      // Fetch timeSlots if the user has viewing rights.
      // This can happen parallel to loadData.
      // We are not interested to return them from loadData call.
      fetchMonthlyTimeSlots(dispatch, listing);
    }

    return Promise.all(followUps).then(() => listingResponse);
  });
};
